import { CommonModule, NgIf } from '@angular/common'
import { Component, OnDestroy, OnInit, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import { Observable, Subject, takeUntil } from 'rxjs'
import { PaginationComponent } from '../template/pagination/pagination.component'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { eps } from '../../../services/url.service'
import {
  selectAllLoginHistories,
  selectLoginHistoryFailedCount,
  selectLoginHistoryLoading,
  selectLoginHistoryLoggedOutCount,
  selectLoginHistorySuccessfulCount,
  selectLoginHistoryTotalCount,
  selectLoginHistoryTotalRowCount
} from '../../../store/login-history/login-history.selectors'
import { loadLoginHistories } from '../../../store/login-history/login-history.actions'
import { selectAllBusinesses } from '../../../store/business/business.selectors'
import { selectAllUsers } from '../../../store/user/user.selectors'
import { loadEmployees, resetEmployeeState } from '../../../store/user/user.actions'
import { NgSelectModule } from '@ng-select/ng-select'
import { HasPermissionDirective } from '../../../directives/has-permission.directive'
import { loadBusinesses } from '../../../store/business/business.actions'

@Component({
  selector: 'app-user-login-history',
  templateUrl: './login-history.component.html',
  styleUrl: './login-history.component.scss',
  imports: [
    CommonModule,
    NgIf,
    FormsModule,
    RouterModule,
    PaginationComponent,
    CapitalizePipe,
    NgSelectModule,
    HasPermissionDirective
  ]
})
export class UserLoginHistoryComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private destroyed$ = new Subject<void>()

  // Login history list
  loginHistory$: Observable<any[]> = this.store.select(selectAllLoginHistories)

  // Summary counts
  totalCount$: Observable<number> = this.store.select(selectLoginHistoryTotalCount)

  successCount$: Observable<number> = this.store.select(selectLoginHistorySuccessfulCount)

  failedCount$: Observable<number> = this.store.select(selectLoginHistoryFailedCount)

  loggedOutCount$: Observable<number> = this.store.select(selectLoginHistoryLoggedOutCount)

  totalRowCount$: Observable<number> = this.store.select(selectLoginHistoryTotalRowCount)

  loading$: Observable<boolean> = this.store.select(selectLoginHistoryLoading)

  // Pagination
  currentPage = 1
  pageLimit = 10

  totalRowCount = 0
  pageLastRecord = 0

  // Filters
  search = ''
  status = ''
  loginMethod = ''

  selectedBusinessId: string | null = null
  selectedEmployeeId: string | null = null

  businesses$ = this.store.select(selectAllBusinesses)
  employees$ = this.store.select(selectAllUsers)
  private businessRetryDone = false
  //   businessesLoading$ = this.store.select(selectBusinessLoading)

  ngOnInit(): void {
    this.totalRowCount$.pipe(takeUntil(this.destroyed$)).subscribe(total => {
      this.totalRowCount = total || 0
      this.updatePageLastRecord()
    })

    this.loadLoginHistoryData()

    this.businesses$.pipe(takeUntil(this.destroyed$)).subscribe(businesses => {
      if (
        this.businessRetryDone === false &&
        Array.isArray(businesses) &&
        businesses.length === 0
      ) {
        this.businessRetryDone = true

        setTimeout(() => {
          this.loadAllBusinesses()
        }, 300)
      }
    })

    this.loadAllBusinesses()
  }

  /**
   * Load login history from API through NgRx
   */
  loadLoginHistoryData(): void {
    const params = new URLSearchParams()

    if (this.selectedBusinessId) {
      params.set('businessId', this.selectedBusinessId)
    }

    if (this.selectedEmployeeId) {
      params.set('userId', this.selectedEmployeeId)
    }

    if (this.status) {
      params.set('status', this.status)
    }

    this.store.dispatch(
      loadLoginHistories({
        params: params.toString(),
        page: this.currentPage,
        limit: this.pageLimit
      })
    )
  }

  /**
   * Search / filter
   */
  setCurrentPage(): void {
    this.currentPage = 1
    this.loadLoginHistoryData()
  }

  /**
   * Pagination
   */
  onPageChange(newPage: number): void {
    if (newPage > 0) {
      this.currentPage = newPage
      this.loadLoginHistoryData()
      this.updatePageLastRecord()
    }
  }

  /**
   * Page size change
   */
  onPageLimitChange(): void {
    this.currentPage = 1
    this.loadLoginHistoryData()
    this.updatePageLastRecord()
  }

  /**
   * Calculate last visible record
   */
  private updatePageLastRecord(): void {
    this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalRowCount)
  }

  /**
   * User initials
   */
  getInitials(firstName: string = '', lastName: string = ''): string {
    const firstInitial = firstName?.charAt(0) || ''
    const lastInitial = lastName?.charAt(0) || ''

    return (firstInitial + lastInitial).toUpperCase()
  }

  /**
   * Status badge class
   */
  getStatusClass(status: string): string {
    switch (status) {
      case 'success':
        return 'badge-success'

      case 'failed':
        return 'badge-danger'

      case 'logged_out':
        return 'badge-warning'

      default:
        return 'badge-secondary'
    }
  }

  /**
   * Status icon
   */
  getStatusIcon(status: string): string {
    switch (status) {
      case 'success':
        return 'ti ti-circle-check'

      case 'failed':
        return 'ti ti-alert-circle'

      case 'logged_out':
        return 'ti ti-logout'

      default:
        return 'ti ti-info-circle'
    }
  }

  /**
   * Status display text
   */
  getStatusLabel(status: string): string {
    switch (status) {
      case 'success':
        return 'Success'

      case 'failed':
        return 'Failed'

      case 'logged_out':
        return 'Logged Out'

      default:
        return status || 'N/A'
    }
  }

  /**
   * Device icon
   */
  getDeviceIcon(deviceType?: string): string {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return 'ti ti-device-mobile'

      case 'tablet':
        return 'ti ti-device-tablet'

      case 'desktop':
        return 'ti ti-device-desktop'

      default:
        return 'ti ti-device-desktop'
    }
  }

  /**
   * Login history details
   *
   * You can later replace this with NgbModal.
   */
  openLoginHistoryDetails(history: any): void {
    console.log('Login history details:', history)
  }

  /**
   * Dashboard route
   */
  getDashboardRoute(): string {
    return eps.dashboardPath
  }

  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
  }

  onBusinessChange(businessId: string | null): void {
    this.selectedBusinessId = businessId || ''
    this.selectedEmployeeId = ''

    this.store.dispatch(resetEmployeeState())

    if (businessId) {
      const params = new URLSearchParams()
      params.set('businessId', businessId)
      params.set('disabled', 'false')

      this.store.dispatch(
        loadEmployees({
          params: params.toString()
        })
      )
    }

    this.currentPage = 1
    this.loadLoginHistoryData()
  }

  onEmployeeChange(employeeId: string | null): void {
    this.selectedEmployeeId = employeeId || ''

    this.currentPage = 1
    this.loadLoginHistoryData()
  }

  customSearchFn = (term: string, item: any) => {
    const fullName = item && item.businessName ? item.businessName.toLowerCase() : ''
    return fullName.includes(term ? term.toLowerCase() : '')
  }

  customSearchFnEmployee = (term: string, item: any) => {
    // Handle undefined item and fields gracefully
    const first = item?.firstName || ''
    const last = item?.lastName || ''
    const fullName = `${first} ${last}`.toLowerCase()

    return fullName.includes(term?.toLowerCase() || '')
  }

  loadAllBusinesses() {
    const params: any = {}
    // params.context = 'modal'
    // if (this.businessIdss?.length) {
    //   params.businessIds = this.businessIdss.join(',')
    // }
    params.disabled = false
    this.store.dispatch(
      loadBusinesses({
        params: new URLSearchParams(params).toString(),
        context: 'modal'
      })
    )
  }
}
